import { use, useMemo } from 'react'
import { navigate } from 'wouter/use-browser-location'
import { makeVar } from '@apollo/client'
import { useReactiveVar } from '@apollo/client/react'
import { utils } from '@guestgoodie/graphql'
import { t } from '@lingui/core/macro'

import {
  AuthClient,
  isSessionReturn,
  ReturnType,
  Session
} from '@guestgoodie/auth-core'

import { client as apolloClient } from '@/components/Apollo'
import { showToast, ToastType } from '@/features/toast'

async function getCsrfToken() {
  return utils.getOrGenerateCsrfToken(import.meta.env.VITE_API_URL)
}

export const client = new AuthClient(import.meta.env.VITE_API_URL, {
  getCsrfToken
})

type SessionPromise = Promise<ReturnType>

export const sessionPromiseVar = makeVar<SessionPromise>(client.check())

let refreshPromise: Promise<Session | null> | null = null

export function refresh() {
  if (!refreshPromise) {
    const promise = client.check()

    refreshPromise = promise
      .then(response => {
        return isSessionReturn(response)
          ? Promise.resolve(response.session)
          : Promise.resolve(null)
      })
      .catch(() => Promise.resolve(null))
      .finally(() => {
        // Update the promise in case we need to handle that the user is not authenticated anymore
        sessionPromiseVar(promise)

        // Clear the promise cache after completion
        refreshPromise = null
      })
  }

  return refreshPromise
}

export function switchOrg(slug: string) {
  return client.switchOrg(slug)
}

export function resetPassword(email: string) {
  return client.resetPassword(email)
}

export function changePassword(code: string, password: string) {
  return client.changePassword({ code, password }).then(() => {
    apolloClient.clearStore()

    showToast({
      id: 'password-change-success',
      type: ToastType.SUCCESS,
      message: t({
        comment:
          'Success Toaster Message when the user successfuly changed their password. They will need to sign-in again after the change of the password.',
        message:
          'The password was changed successfully, you can now sign-in with your new password.'
      })
    })

    // Need to redirect to sign-in
    navigate('/app/sign-in')
  })
}

export function signOut() {
  apolloClient.clearStore()
  client.signOut()
}

export default function useSuspendedSession() {
  const promise = useReactiveVar(sessionPromiseVar)
  const response = use(promise)

  return useMemo(() => {
    if (isSessionReturn(response)) return response.session
    else return null
  }, [response])
}
